import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { userService } from 'components/api/users';
import { User, UsersParams, UsersResponse } from 'types/user';

interface OnlineUsersState {
  data: User[];
  total: number;
  isLoading: boolean;
  error: string | null;
  params: UsersParams;
}

const initialState: OnlineUsersState = {
  data: [],
  total: 0,
  isLoading: false,
  error: null,
  params: {
    page: 1,
    limit: 10,
    sortField: 'id',
    sortValue: 'DESC',
    siteId: 1,
  },
};

export const fetchOnlineUsers = createAsyncThunk(
  'onlineUsers/fetchOnlineUsers',
  async (params: UsersParams, { rejectWithValue }) => {
    try {
      const response = await userService.getOnlineUsers(params);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to fetch online users',
      );
    }
  },
);

const onlineUsersSlice = createSlice({
  name: 'onlineUsers',
  initialState,
  reducers: {
    setOnlineParams: (state, action: PayloadAction<Partial<UsersParams>>) => {
      state.params = { ...state.params, ...action.payload };
    },
    resetOnlineParams: (state) => {
      state.params = initialState.params;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOnlineUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOnlineUsers.fulfilled, (state, action: PayloadAction<UsersResponse>) => {
        state.isLoading = false;
        state.data = action.payload.data?.docs || [];
        state.total = action.payload.data?.totalDocs || 0;
      })
      .addCase(fetchOnlineUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setOnlineParams, resetOnlineParams } = onlineUsersSlice.actions;
export default onlineUsersSlice.reducer;
